<script>
  import { required, maxLength, minLength, email } from 'vuelidate/lib/validators'
  import { enrollment, exactLength } from '@/validators'
  import SchoolApi from '@/services/Api/SchoolApi'

  export default {
    data() {
      return {
        loading: false,
        registered: false,
        school_loading: true,
        schools: [],
        new_user: this._getUserFields()
      }
    },

    computed: {
      enrollment_is_email() {
        return this.new_user.school && this.new_user.school.enrollment_type === 'EMAIL'
      }
    },

    validations() {
      if (!this.new_user.school) {
        return {}
      }

      var rules = {
        new_user: {
          firstname: {
            required,
            maxLength: maxLength(150)
          },
          lastname: {
            required,
            maxLength: maxLength(150)
          },
          league: {
            required
          },
          promo: {
            required,
            exactLength: exactLength(4)
          },
          email: {
            required,
            email,
            maxLength: maxLength(150)
          },
          password: {
            required,
            minLength: minLength(8)
          }
        }
      }

      if (this.enrollment_is_email) {
        rules.new_user.email.enrollment = enrollment(
          this.new_user.school.enrollment_type,
          this.new_user.school.enrollment_rule
        )
      }

      return rules
    },

    created() {
      SchoolApi.getAll()
        .then(schools => (this.schools = schools))
        .finally(schools => (this.school_loading = false))
    },

    methods: {
      saveUser() {
        this.$v.$touch()

        if (this.$v.$error) {
          return
        }

        this.loading = true
        this.$auth.register({
          data: {
            school_id: this.new_user.school.id,
            league_id: this.new_user.league,
            graduating_year: this.new_user.promo,
            firstname: this.new_user.firstname,
            lastname: this.new_user.lastname,
            email: this.new_user.email.toLowerCase(),
            password: this.new_user.password
          },
          success(response) {
            this.loading = false
            this.registered = true
            this.resetForm()
            // this.$store.dispatch('Alerts/add', {
            //   trans: 'messages.register_success',
            //   duration: 10000,
            //   type: 'success'
            // })
          },
          error(e) {
            this.loading = false

            if (this.$isError(e, 422) && e.response.data.email) {
              this.$store.dispatch('Alerts/add', {
                trans: e.response.data.email[0] + '-email',
                duration: 10000,
                type: 'danger'
              })
            } else {
              this.$handleErrors(e)
            }
          }
        })
      },

      resetForm() {
        this.$v.$reset()
        this.new_user = this._getUserFields()
      },

      _getUserFields() {
        return {
          school: '',
          league: '',
          promo: '',
          firstname: '',
          lastname: '',
          email: '',
          password: ''
        }
      }
    }
  }
</script>

<template>
  <section class="Register">
    <form class="Register_form"
      @submit.prevent="saveUser"
      novalidate>
      <h1 :title="$t('title')" v-once>
        <router-link :to="{name: 'home'}">
          <img src="../assets/img/logo_small.svg" :alt="$t('title')">
        </router-link>
      </h1>

      <div class="content has-text-centered" v-once>
        <p v-html="$t('register.form_text')"></p>
      </div>

      <p class="app-simple-loader" v-if="school_loading"></p>

      <template v-if="!school_loading">
        <div class="field">
          <div class="control has-icons-left">
            <div class="select">
              <select v-model="new_user.school">
                <option value="">{{ $t('register.field.school') }}</option>
                <option v-for="school in schools"
                  :value="school"
                  :key="school.id">
                  {{ school.name }}
                </option>
              </select>
            </div>
        
            <span class="icon">
              <i class="material-icons">school</i>
            </span>
          </div>
        </div>
        
        <article class="content message is-info"
          v-if="!new_user.school"
          v-once>
          <div class="message-body">
            <p>{{ $t('register.no_school') }}</p>
            <a href="https://docs.google.com/forms/d/e/1FAIpQLSdV8fDG_u4auTCNUGr-RpgTCu5m-eKsdRLnCcffBNvlSVLU6A/viewform"
              target="_blank"
              class="button is-danger">
              {{ $t('buttons.add_my_school') }}    
            </a>
          </div>
        </article>
      </template>

      <template v-if="new_user.school">
        <div class="columns">
          <div class="field column">
            <div class="control has-icons-left">
              <div class="select"
                :class="{'is-danger': $v.new_user.league.$error}">
                <select v-model="new_user.league">
                  <option value="">{{ $t('register.field.league') }}</option>
                  <option v-for="league in new_user.school.leagues"
                    :key="league.id"
                    :value="league.id">
                    {{ league.name }}
                  </option>
                </select>
              </div>
          
              <span class="icon">
                <i class="material-icons">school</i>
              </span>
            </div>

            <p class="help is-danger"
              v-show="$v.new_user.league.$error && !$v.new_user.league.required">
              {{ $t('validation.required') }}
            </p>
          </div>
          
          <div class="field column">
            <p class="control has-icon">
              <input class="input"
                :class="{'is-danger': $v.new_user.promo.$error}"
                type="number"
                v-model.number="new_user.promo"
                :placeholder="$t('register.field.promo')">
          
              <span class="icon">
                <i class="material-icons">school</i>
              </span>
            </p>

            <p class="help is-danger"
              v-show="$v.new_user.promo.$error && !$v.new_user.promo.required">
              {{ $t('validation.required') }}
            </p>
            <p class="help is-danger"
              v-show="$v.new_user.promo.$error && !$v.new_user.promo.exactLength">
              {{ $t('validation.size.string', {size: 4}) }}
            </p>
          </div>
        </div>
        
        <div class="columns">
          <div class="field column">
            <p class="control has-icon">
              <input class="input"
                :class="{'is-danger': $v.new_user.firstname.$error}"
                type="text"
                v-model="new_user.firstname"
                :placeholder="$t('register.field.firstname')">
          
              <span class="icon">
                <i class="material-icons">account_circle</i>
              </span>
            </p>

            <p class="help is-danger"
              v-show="$v.new_user.firstname.$error && !$v.new_user.firstname.required">
              {{ $t('validation.required') }}
            </p>
            <p class="help is-danger"
              v-show="$v.new_user.firstname.$error && !$v.new_user.firstname.maxLength">
              {{ $t('validation.max.string', {max: 190}) }}
            </p>
          </div>
          
          <div class="field column">
            <p class="control has-icon">
              <input class="input"
                :class="{'is-danger': $v.new_user.lastname.$error}"
                type="text"
                v-model="new_user.lastname"
                :placeholder="$t('register.field.lastname')">
          
              <span class="icon">
                <i class="material-icons">account_circle</i>
              </span>
            </p>

            <p class="help is-danger"
              v-show="$v.new_user.lastname.$error && !$v.new_user.lastname.required">
              {{ $t('validation.required') }}
            </p>
            <p class="help is-danger"
              v-show="$v.new_user.lastname.$error && !$v.new_user.lastname.maxLength">
              {{ $t('validation.max.string', {max: 190}) }}
            </p>
          </div>
        </div>

        <div class="field">
          <p class="control has-icon">
            <input class="input"
              :class="{'is-danger': $v.new_user.email.$error}"
              type="email"
              v-model="new_user.email"
              :placeholder="$t('register.field.email')">
        
            <span class="icon">
              <i class="material-icons">email</i>
            </span>
          </p>

          <p class="help is-danger"
            v-show="$v.new_user.email.$error && !$v.new_user.email.required">
            {{ $t('validation.required') }}
          </p>
          <p class="help is-danger"
            v-show="$v.new_user.email.$error && !$v.new_user.email.email">
            {{ $t('validation.email') }}
          </p>
          <p class="help is-danger"
            v-show="$v.new_user.email.$error && !$v.new_user.email.maxLength">
            {{ $t('validation.max.string', {max: 190}) }}
          </p>
          <p class="help is-danger"
            v-if="enrollment_is_email"
            v-show="$v.new_user.email.$error && !$v.new_user.email.enrollment">
            {{ $t('validation.school_email', {school: new_user.school.enrollment_rule}) }}
          </p>
        </div>

        <div class="field">
          <p class="control has-icon">
            <input class="input"
              :class="{'is-danger': $v.new_user.password.$error}"
              type="password"
              v-model="new_user.password"
              :placeholder="$t('register.field.password')">
        
            <span class="icon">
              <i class="material-icons">lock_outline</i>
            </span>
          </p>

          <p class="help is-danger"
            v-show="$v.new_user.password.$error && !$v.new_user.password.required">
            {{ $t('validation.required') }}
          </p>
          <p class="help is-danger"
            v-show="$v.new_user.password.$error && !$v.new_user.password.minLength">
            {{ $t('validation.min.string', {min: 8}) }}
          </p>
        </div>

        <div>
          <button class="button is-danger is-fullwidth"
            :class="{'is-loading': loading}"
            type="submit">
            {{ $t('buttons.register') }}
          </button>
        </div>
      </template>
    </form>

    <article class="Register_intro"
      v-once>
      <p class="Register_intro_actions has-text-centered">
        <span>{{ $t('register.already_account') }}</span>
        <router-link 
          :to="{'name': 'login'}" 
          class="button is-primary">
          {{ $t('buttons.login') }}
        </router-link>
      </p>

      <div class="content has-text-centered">
        <figure><img src="../assets/img/illus/share.png"></figure>
        <h1>{{ $t('register.title') }}</h1>
        <div v-html="$t('register.description')"></div>
      </div>
    </article>

    <b-modal :active.sync="registered" has-modal-card>
      <div class="modal-card">
        <div class="modal-card-head">
          <h1 class="modal-card-title">Bravo !</h1>
          <button class="delete"
            type="button"
            @click="registered = false"></button>
        </div>
        <div class="card">
          <div class="card-content content">
            <h2 v-once>{{ $t('modals.registered.title') }}</h2>
            <p v-html="$t('modals.registered.text')" v-once></p>
          </div>
        </div>
      </div>
    </b-modal>
  </section>
</template>

<style lang="scss">
  @import '~bulma/sass/utilities/all';

  .app-page-register {
    overflow: hidden;
  }

  .Register {
    .columns,
    .field {
      margin-bottom: pxToEm(30);
    }

    .columns .field {
      margin-bottom: 0;
    }

    .column {
      padding-bottom: 0;
    }

    &_form {
      overflow: auto;
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      max-width: $auth-form-width;
      padding: pxToEm(20);
      background: #fff;

      h1 {
        margin: pxToEm(40) pxToEm(40) pxToEm(80);

        img {
          display: block;
          width: 160px;
          margin: auto;
        }
      }
    }

    &_intro {
      overflow: auto;
      display: flex;
      flex-direction: column;
      justify-content: center;
      position: absolute;
      top: 0;
      right: 0;
      left: $auth-form-width;
      height: 100%;
      padding: pxToEm(100) pxToEm(60) pxToEm(40);
      background: $yellow url('../assets/img/bg-banner-home-last.jpg') repeat;

      &_actions {
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        padding: pxToEm(20);

        .button {
          margin-left: pxToEm(20);
        }
      }

      figure img {
        max-height: 220px;
      }
    }
  }

  @include mobile() {
    .app-page-register {
      overflow: auto;
    }

    .Register {
      &_form,
      &_intro {
        position: static;
        width: 100%;
        max-width: none;
      }

      &_form {
        > .field,
        .columns {
          margin-bottom: pxToEm(10);
        }
      }

      &_intro {
        padding: pxToEm(20) pxToEm(20) pxToEm(60);

        &_actions {
          position: static;
          flex-direction: column;
          padding: 0;

          .button {
            margin: pxToEm(20) 0;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// PageRegister.vue?49c1a2e2